import { useState } from 'react';
import { useAppStore } from '../store';
import { settingsApi } from '../services/api';
import type { LLMProvider } from '../types';

export default function SettingsPage() {
  const { user, settings, setUser, setSettings } = useAppStore();
  const [llmProvider, setLlmProvider] = useState<LLMProvider>(user?.llmProvider || 'gemini');
  const [llmApiKey, setLlmApiKey] = useState('');
  const [isSavingLlm, setIsSavingLlm] = useState(false);
  const [isRemovingKey, setIsRemovingKey] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  const handleSaveLlmKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!llmApiKey.trim()) return;
    
    setIsSavingLlm(true);
    setMessage(null);
    
    try {
      const response = await settingsApi.setLlmKey({
        llmProvider,
        llmApiKey: llmApiKey.trim(),
      });
      
      if (response.success && response.data && user) {
        setUser({
          ...user,
          llmProvider: response.data.llmProvider,
          hasLlmApiKey: response.data.hasLlmApiKey,
        });
        setLlmApiKey('');
        setMessage({ type: 'success', text: 'API key saved successfully!' });
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to save API key',
      });
    } finally {
      setIsSavingLlm(false);
    }
  };
  
  const handleRemoveLlmKey = async () => {
    if (!confirm('Are you sure you want to remove your API key?')) return;
    
    setIsRemovingKey(true);
    setMessage(null);
    
    try {
      const response = await settingsApi.removeLlmKey();
      if (response.success && user) {
        setUser({
          ...user,
          hasLlmApiKey: false,
        });
        setMessage({ type: 'success', text: 'API key removed' });
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to remove API key',
      });
    } finally {
      setIsRemovingKey(false);
    }
  };
  
  const handleToggleAutoBreakdown = async () => {
    if (!settings) return;
    
    try {
      const response = await settingsApi.update({
        autoBreakdownEnabled: !settings.autoBreakdownEnabled,
      });
      
      if (response.success && response.data) {
        setSettings(response.data);
      }
    } catch (err) {
      console.error('Failed to update settings:', err);
    }
  };
  
  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-bold text-slate-900 mb-6">Settings</h1>
      
      {message && (
        <div
          className={`mb-6 p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          {message.text}
        </div>
      )}
      
      {/* Notion Connection */}
      <section className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Notion Connection</h2>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-slate-900">Workspace</p>
              <p className="text-sm text-slate-600">{user?.notionWorkspaceName}</p>
            </div>
            <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm font-medium">
              Connected
            </span>
          </div>
          
          <div>
            <p className="font-medium text-slate-900">Tasks Database</p>
            <p className="text-sm text-slate-600">
              {user?.notionTaskDatabaseId ? (
                <span className="font-mono text-xs">{user.notionTaskDatabaseId}</span>
              ) : (
                <span className="text-amber-600">Not configured</span>
              )}
            </p>
          </div>
        </div>
      </section>
      
      {/* LLM Configuration */}
      <section className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">AI Configuration</h2>
        
        <form onSubmit={handleSaveLlmKey} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              LLM Provider
            </label>
            <select
              value={llmProvider}
              onChange={(e) => setLlmProvider(e.target.value as LLMProvider)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
            >
              <option value="gemini">Google Gemini</option>
              <option value="openai">OpenAI (GPT-4o)</option>
              <option value="claude">Anthropic Claude</option>
            </select>
            <p className="text-xs text-slate-500 mt-1">
              {llmProvider === 'gemini' && 'Free tier available with 500 requests/day'}
              {llmProvider === 'openai' && 'Requires OpenAI API key'}
              {llmProvider === 'claude' && 'Requires Anthropic API key'}
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              API Key
            </label>
            {user?.hasLlmApiKey ? (
              <div className="flex items-center gap-2">
                <input
                  type="password"
                  value="••••••••••••••••"
                  disabled
                  className="flex-1 px-4 py-2 border border-slate-300 rounded-lg bg-slate-50"
                />
                <button
                  type="button"
                  onClick={handleRemoveLlmKey}
                  disabled={isRemovingKey}
                  className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  {isRemovingKey ? 'Removing...' : 'Remove'}
                </button>
              </div>
            ) : (
              <input
                type="password"
                value={llmApiKey}
                onChange={(e) => setLlmApiKey(e.target.value)}
                placeholder="Enter your API key"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
              />
            )}
            <p className="text-xs text-slate-500 mt-1">
              Your API key is encrypted and stored securely
            </p>
          </div>
          
          {!user?.hasLlmApiKey && (
            <button
              type="submit"
              disabled={isSavingLlm || !llmApiKey.trim()}
              className="px-4 py-2 bg-ocean-600 text-white rounded-lg hover:bg-ocean-700 transition-colors disabled:opacity-50"
            >
              {isSavingLlm ? 'Saving...' : 'Save API Key'}
            </button>
          )}
        </form>
      </section>
      
      {/* Automation Settings */}
      <section className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Automation</h2>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-slate-900">Auto Task Breakdown</p>
              <p className="text-sm text-slate-600">
                Automatically break down complex tasks into subtasks
              </p>
            </div>
            <button
              onClick={handleToggleAutoBreakdown}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings?.autoBreakdownEnabled ? 'bg-ocean-600' : 'bg-slate-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings?.autoBreakdownEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </section>
      
      {/* Get API Keys Section */}
      <section className="mt-6 p-4 bg-slate-50 rounded-xl">
        <h3 className="font-medium text-slate-900 mb-2">Need an API key?</h3>
        <ul className="space-y-2 text-sm text-slate-600">
          <li>
            <a
              href="https://makersuite.google.com/app/apikey"
              target="_blank"
              rel="noopener noreferrer"
              className="text-ocean-600 hover:underline"
            >
              Get Gemini API key →
            </a>
            <span className="text-slate-500"> (Free tier available)</span>
          </li>
          <li>
            <a
              href="https://platform.openai.com/api-keys"
              target="_blank"
              rel="noopener noreferrer"
              className="text-ocean-600 hover:underline"
            >
              Get OpenAI API key →
            </a>
          </li>
          <li>
            <a
              href="https://console.anthropic.com/settings/keys"
              target="_blank"
              rel="noopener noreferrer"
              className="text-ocean-600 hover:underline"
            >
              Get Claude API key →
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}


